import React, { memo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Table from "ui-components/table/table";
import { useDispatchBoardQueue } from "./hooks/useDispatchBoardQueue";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import { getCityDeliveryDispatchBoardQueueColumns } from "./cityDeliveryDispatchBoardQueueColumns";
import { isCityDeliveryDispatchAllowed } from "./allowedTerminals";
import { Routes, terminalNumberParamName } from "app/route/RoutesConfig";
import "./cityDeliveryDispatchBoard.scss";
import { DispatchBoardQueueLoad } from "app/data/city-delivery/dispatch-board/models";
import CityDeliveryDispatchWorkflow from "./workflow/cityDeliveryDispatchWorkflow";
import ReloadButton from "ui-components/reload-button/reloadButton";

const CityDeliveryDispatchBoard: React.FC<{}> = memo(() => {
  const [selectedDispatchLoad, setSelectedDispatcLoad] = useState<DispatchBoardQueueLoad>();
  const userState: UserState = useSelector(userSelector);
  const history = useHistory();
  const params = useParams<{ [key: string]: string }>();
  const activeTerminalId = userState.activeTerminal?.id;
  const hasAccess = isCityDeliveryDispatchAllowed(activeTerminalId);

  const {
    dispatchBoardQueueData,
    isLoadingDispatchBoardQueue,
    refetchDispatchBoardQueue,
    lastUpdated
  } = useDispatchBoardQueue({
    terminalNumber: hasAccess ? activeTerminalId : undefined,
  });

  useEffect(() => {
    if (!hasAccess) {
      const terminalId = params[terminalNumberParamName] ?? activeTerminalId;
      const homePath = terminalId != null ? `/${terminalId}${Routes.home}` : Routes.home;
      history.replace(homePath);
    }
  }, [hasAccess, history, params, activeTerminalId]);

  if (!hasAccess) {
    return null;
  }

  const formattedTime = lastUpdated
    ? (() => {
      const date = new Date(lastUpdated);

      const datePart = date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });

      const timePart = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      return `${datePart} • ${timePart}`;
    })()
    : null;


  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="City Delivery Dispatch Board"
          mods={{ "full-width": true }}
          // style={{ justifyContent: "end", width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "6px",
              justifyContent: "end",
              width: "100%"
            }}
          >
            {formattedTime && (
              <span style={{ fontSize: "14px", color: "#777" }}>
                Last Refreshed: {formattedTime}
              </span>
            )}
            <ReloadButton
              title="Refresh Dispatch"
              spinner={isLoadingDispatchBoardQueue}
              disabled={isLoadingDispatchBoardQueue}
              onClick={async () => {
                await refetchDispatchBoardQueue();
              }}
            />
          </div>
        </ContentContainerToolbar >
      }
    >
      <div className="xgs-city-delivery-dispatch-board">
        <div className="xgs-city-delivery-dispatch-board__table-wrapper">
          <Table
            isLoading={isLoadingDispatchBoardQueue}
            columns={getCityDeliveryDispatchBoardQueueColumns()}
            data={dispatchBoardQueueData?.data?.loads || []}
            ignoreCellClick="selection"
            cursorPointer={true}
            noResultsText="No data available"
            onSelectedRowsChange={null}
            onRowClicked={(row: DispatchBoardQueueLoad) => { setSelectedDispatcLoad(row) }}
            responsive={true}
            sorting={true}
            enableMultiSort={true}
            autoResetSelectedRows={true}
            keepTableOnLoading={true}
            autoHeight={true}
          />
        </div>
        {(selectedDispatchLoad && (selectedDispatchLoad.picklistNumber || selectedDispatchLoad.manifestNumber)) && (
          <CityDeliveryDispatchWorkflow
            dispatchLoad={selectedDispatchLoad}
            onClose={() => {
              setSelectedDispatcLoad(undefined);
              refetchDispatchBoardQueue();
            }}
          />
        )}
      </div>
    </ContentContainer >
  );
});

export default CityDeliveryDispatchBoard;